<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isUUID } from 'class-validator';
import PhoneNumberFieldV2 from '@satellite/components/elements/forms/PhoneNumberFieldV2.vue';

import {
  AssetContainerType,
  AssetVisitType,
  LoadTypeDirection,
  type LoadTypeOperation,
  ObservationMatchAttribute,
  WarehouseFeatureEnum,
  isFeatureEnabledForWarehouse,
  isPhoneValid,
  type IWarehouse
} from '@nova/core';

import { useCheckinForms } from '@/components/composables';
import CustomFields from '@/components/custom_fields/CustomFields.vue';
import CompanySelect from '@/components/CompanySelect.vue';
import AssetDetailsForm from '@/components/AssetDetailsForm.vue';
import LicensePlateSelect from '@/components/LicensePlateSelect.vue';
import DotSelect from '@/components/DotSelect.vue';
import { useCheckinFlow, type AssetContainerData } from '@/composables/useCheckinFlow';
import { resolveMessagingSettings } from '@/composables/useMessagingSettings';
import { getLoadTypeDirectionI18nKey, getLoadTypeOperationI18nKey } from '@/utils/assetVisitType';

defineOptions({ name: 'CheckinDetails' });

const props = defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

const { t, locale } = useI18n();
const checkinFlow = useCheckinFlow();
const {
  additionalAssetDetailsFields,
  additionalAssetDetailsFieldsExist,
  additionalAssetDetailsTriggers,
  areRequiredAssetDetailsFilled,
  extractFieldsFromTriggers,
  prepareCustomFormsData
} = useCheckinForms(props.warehouse as IWarehouse, checkinFlow.loadTypeId.value);

const messagingSettings = computed(() => resolveMessagingSettings(props.warehouse));

const loadType = computed(() => checkinFlow.appointment.value?.loadType);

// The dock is only meaningful to the driver when the load type let the carrier pick it; otherwise
// it is an internal assignment that can still change before arrival.
const dock = computed(() =>
  loadType.value?.settings?.allowCarrierDockSelection === true
    ? checkinFlow.appointment.value?.dock
    : null
);

function getDirectionArrowIcon(direction: LoadTypeDirection): string {
  if (direction === LoadTypeDirection.Inbound) {
    return 'mdi-arrow-left-thin';
  }
  if (direction === LoadTypeDirection.Outbound) {
    return 'mdi-arrow-right-thin';
  }
  return 'mdi-swap-horizontal';
}

function getDirectionLabel(direction: LoadTypeDirection): string {
  const key = getLoadTypeDirectionI18nKey(direction);
  return key ? t(`initial.direction.${key}`) : direction;
}

function getOperationLabel(operation: LoadTypeOperation): string {
  const key = getLoadTypeOperationI18nKey(operation);
  return key ? t(`initial.operation.${key}`) : operation;
}

const isYMSEnabled = computed(() =>
  isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.YMS)
);

const shouldShowAssetDetailsForm = computed(
  () => checkinFlow.assetVisitType.value !== AssetVisitType.PickUp
);

const isSmartGateIntegrationEnabled = computed(() =>
  isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.SmartGate)
);

const observationMatchAttribute = computed(
  () => props.warehouse.settings?.observationEventMatchAttribute
);

const showAnySmartGateFields = computed(
  () =>
    observationMatchAttribute.value === ObservationMatchAttribute.ANY ||
    observationMatchAttribute.value === ObservationMatchAttribute.CASCADE
);

const showLicensePlateSmartGateField = computed(
  () =>
    observationMatchAttribute.value === ObservationMatchAttribute.LICENSE_PLATE ||
    showAnySmartGateFields.value
);

const showDotNumberSmartGateField = computed(
  () =>
    observationMatchAttribute.value === ObservationMatchAttribute.DOT_NUMBER ||
    showAnySmartGateFields.value
);

const isDotNumberRequired = computed(
  () => observationMatchAttribute.value !== ObservationMatchAttribute.CASCADE
);

const isRefNumberRequired = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceRequired === true
);

const assetContainerTypes = computed(() => [
  { title: t('appointmentForm.containerTrailer'), value: AssetContainerType.Trailer },
  { title: t('appointmentForm.containerContainer'), value: AssetContainerType.Container },
  { title: t('appointmentForm.containerOversized'), value: AssetContainerType.Oversized },
  { title: t('appointmentForm.containerOther'), value: AssetContainerType.Other }
]);

const phoneFieldRules = computed(() => [
  (
    value: string,
    phone: { valid?: boolean } | null | undefined,
    messageOptions?: { example?: string }
  ) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return t('common.validation.fieldRequired');
    }
    if (phone?.valid) {
      return true;
    }
    const { example } = messageOptions ?? {};
    return example
      ? t('common.validation.phoneInvalidWithExample', { example })
      : t('common.validation.phoneInvalid');
  }
]);

const licensePlateRules = computed(() => {
  if (observationMatchAttribute.value === ObservationMatchAttribute.ANY) {
    return [
      (value: { name?: string } | null) => {
        const licensePlateValue = value?.name?.trim() || '';
        const dotNumberValue = checkinFlow.dotNumber.value?.trim() || '';
        return licensePlateValue || dotNumberValue
          ? true
          : t('appointmentForm.licenseOrDotRequired');
      }
    ];
  }
  if (
    observationMatchAttribute.value === ObservationMatchAttribute.LICENSE_PLATE ||
    observationMatchAttribute.value === ObservationMatchAttribute.CASCADE
  ) {
    return [
      (value: { name?: string } | null) =>
        value?.name?.trim() ? true : t('common.validation.fieldRequired')
    ];
  }
  return [];
});

const dotNumberRules = computed(() => {
  if (observationMatchAttribute.value === ObservationMatchAttribute.ANY) {
    return [
      (value: { name?: string } | null) => {
        const dotNumberValue = value?.name?.trim() || '';
        const licensePlateValue = checkinFlow.licensePlate.value?.trim() || '';
        return dotNumberValue || licensePlateValue
          ? true
          : t('appointmentForm.licenseOrDotRequired');
      }
    ];
  }
  if (observationMatchAttribute.value === ObservationMatchAttribute.DOT_NUMBER) {
    return [
      (value: { name?: string } | null) =>
        value?.name?.trim() ? true : t('common.validation.fieldRequired')
    ];
  }
  return [];
});

const companySelectModel = computed(() => {
  if (!checkinFlow.companyId.value && !checkinFlow.companyName.value) {
    return null;
  }
  return { id: checkinFlow.companyId.value, name: checkinFlow.companyName.value ?? '' };
});

const licensePlateSmartGateModel = computed(() =>
  checkinFlow.licensePlate.value ? { name: checkinFlow.licensePlate.value } : null
);

const dotNumberSmartGateModel = computed(() =>
  checkinFlow.dotNumber.value ? { name: checkinFlow.dotNumber.value } : null
);

function handleCompanySelected(value: { id?: string | null; name?: string } | null) {
  if (!value?.name) {
    checkinFlow.setCompany(null, null);
    return;
  }
  const id = isUUID(value.id) ? (value.id as string) : null;
  checkinFlow.setCompany(id, (value.name ?? '').trim() || null);
}

function handleLicensePlateSelected(value: { name?: string } | null) {
  checkinFlow.setLicensePlate((value?.name ?? '').trim() || null);
}

function handleDotSelected(value: { name?: string } | null) {
  checkinFlow.setDotNumber((value?.name ?? '').trim() || null);
}

function handleAssetContainerDataChange(value: AssetContainerData[]) {
  checkinFlow.setAssetContainerData(value);
}

watch(
  () => props.warehouse,
  warehouse => {
    checkinFlow.setMessagingType(resolveMessagingSettings(warehouse).messagingType);
  },
  { immediate: true }
);

watch(
  () => checkinFlow.messagingType.value,
  value => checkinFlow.setUseWhatsApp(value === 'WhatsApp'),
  { immediate: true }
);

onMounted(() => {
  additionalAssetDetailsFields.value = extractFieldsFromTriggers(
    additionalAssetDetailsTriggers.value
  );
});

const isAssetContainerDataValid = computed(() => {
  if (!isYMSEnabled.value || !shouldShowAssetDetailsForm.value) {
    return true;
  }
  return checkinFlow.assetContainerData.value.every(
    ({ containerCode, containerType }) => containerCode.trim().length > 0 && containerType
  );
});

const isSmartGateValid = computed(() => {
  if (!isSmartGateIntegrationEnabled.value) {
    return true;
  }
  switch (observationMatchAttribute.value) {
    case ObservationMatchAttribute.ANY:
      return Boolean(checkinFlow.licensePlate.value || checkinFlow.dotNumber.value);
    case ObservationMatchAttribute.CASCADE:
    case ObservationMatchAttribute.LICENSE_PLATE:
      return Boolean(checkinFlow.licensePlate.value);
    case ObservationMatchAttribute.DOT_NUMBER:
      return Boolean(checkinFlow.dotNumber.value);
    default:
      return true;
  }
});

const isFormValid = computed(() => {
  const isPhoneValidated = isPhoneValid(checkinFlow.phoneNumber.value?.trim() ?? '');
  const isCompanySelected = Boolean(checkinFlow.companyId.value || checkinFlow.companyName.value);
  const isRefNumberValid =
    !isRefNumberRequired.value || Boolean(checkinFlow.identifier.value?.trim());

  return (
    isAssetContainerDataValid.value &&
    areRequiredAssetDetailsFilled.value &&
    isPhoneValidated &&
    isCompanySelected &&
    isRefNumberValid &&
    isSmartGateValid.value
  );
});

watch(
  additionalAssetDetailsFields,
  () => {
    checkinFlow.setCustomFormsData(prepareCustomFormsData());
  },
  { deep: true, immediate: true }
);

defineExpose({ isFormValid });
</script>

<template>
  <div class="checkin__form-container d-flex flex-column gap-6">
    <v-card
      v-if="loadType"
      variant="outlined"
      class="comet-i18n-exclude pa-4 d-flex flex-column gap-3 bg--color-neutral-10 border--color-neutral-40"
      data-testid="comet-checkin-load-type-details">
      <div class="d-flex gap-1">
        <span class="typography-body-md-bold">{{ t('initial.loadTypeLabel') }}:</span>
        <span class="typography-body-md">{{ loadType.name }}</span>
      </div>
      <div class="d-flex align-center gap-1">
        <span class="typography-body-md-bold">{{ t('initial.directionLabel') }}:</span>
        <span class="typography-body-md">{{ getDirectionLabel(loadType.direction) }}</span>
        <div class="d-flex align-center rounded-sm bg--color-primary-20 pa-1">
          <v-icon size="14" :icon="getDirectionArrowIcon(loadType.direction)"></v-icon>
          <v-icon size="14" icon="mdi-truck-outline"></v-icon>
        </div>
      </div>
      <div v-if="loadType.operation" class="d-flex gap-1">
        <span class="typography-body-md-bold">{{ t('initial.operationLabel') }}:</span>
        <span class="typography-body-md">{{ getOperationLabel(loadType.operation) }}</span>
      </div>
      <div v-if="dock" class="d-flex gap-1">
        <span class="typography-body-md-bold">{{ t('initial.dockLabel') }}:</span>
        <span class="typography-body-md">{{ dock.name }}</span>
      </div>
    </v-card>

    <asset-details-form
      v-if="isYMSEnabled && shouldShowAssetDetailsForm"
      :asset-container-data="checkinFlow.assetContainerData.value"
      :asset-container-types="assetContainerTypes"
      :max-container-data="3"
      @update:asset-container-data="handleAssetContainerDataChange" />

    <company-select
      :warehouse-id="warehouse.id"
      :model-value="companySelectModel"
      @update:model-value="handleCompanySelected" />

    <div class="custom-field comet-i18n-exclude">
      <label class="custom-field__label required">
        {{ t('appointmentForm.driverPhoneLabel') }}
      </label>
      <phone-number-field-v2
        :model-value="checkinFlow.phoneNumber.value ?? ''"
        @update:model-value="checkinFlow.setPhoneNumber($event)"
        :default-country="warehouse.country || 'USA'"
        :input-props="{ 'data-testid': 'driver-phone-number-field' }"
        :rules="phoneFieldRules" />

      <v-radio-group
        inline
        hide-details
        class="messaging-options"
        :model-value="checkinFlow.messagingType.value"
        @update:model-value="checkinFlow.setMessagingType($event)"
        color="primary">
        <v-radio value="SMS" :disabled="!messagingSettings.isSMSEnabled">
          <template #label><span class="typography-body-sm">SMS</span></template>
        </v-radio>
        <v-radio value="WhatsApp" :disabled="!messagingSettings.isWhatsAppEnabled">
          <template #label><span class="typography-body-sm">WhatsApp</span></template>
        </v-radio>
      </v-radio-group>

      <v-banner v-if="checkinFlow.useWhatsApp.value" class="whatsapp-banner comet-i18n-exclude">
        <v-icon size="24">mdi-whatsapp</v-icon>
        <div class="d-flex flex-column gap-5">
          <div class="d-flex flex-column gap-1">
            <div class="typography-body-md-bold">
              {{ t('appointmentForm.activateWhatsAppTitle') }}
            </div>
            <div class="typography-body-md">
              {{ t('appointmentForm.activateWhatsAppBody') }}
            </div>
          </div>
          <div class="typography-body-md-bold">
            {{ t('appointmentForm.whatsappDelayBanner') }}
          </div>
        </div>
      </v-banner>
    </div>

    <custom-fields
      v-if="additionalAssetDetailsFieldsExist"
      :key="`custom-forms-${locale}`"
      :warehouse="warehouse"
      :value="additionalAssetDetailsFields" />

    <license-plate-select
      v-if="isSmartGateIntegrationEnabled && showLicensePlateSmartGateField"
      :warehouse-id="warehouse.id"
      :model-value="licensePlateSmartGateModel"
      @update:model-value="handleLicensePlateSelected"
      :rules="licensePlateRules" />

    <dot-select
      v-if="isSmartGateIntegrationEnabled && showDotNumberSmartGateField"
      :warehouse-id="warehouse.id"
      :model-value="dotNumberSmartGateModel"
      :required="isDotNumberRequired"
      @update:model-value="handleDotSelected"
      :rules="dotNumberRules" />

    <div class="custom-field comet-i18n-exclude">
      <label class="custom-field__label">{{ t('appointmentForm.notesLabel') }}</label>
      <v-textarea
        color="primary"
        variant="outlined"
        data-testid="comet-appointment-notes"
        :model-value="checkinFlow.notes.value"
        @update:model-value="checkinFlow.setNotes($event)"
        :placeholder="t('appointmentForm.notesPlaceholder')"
        maxlength="4096"
        counter="4096"
        hide-details="auto"
        clearable />
    </div>
  </div>
</template>

<style scoped lang="scss">
.checkin__form-container {
  :deep(.v-input:not(.v-input--error) .v-input__details) {
    min-height: 0;
    padding-top: 0;

    .v-messages {
      min-height: 0;
    }
  }

  :deep(.v-textarea .v-input__details) {
    padding-top: $spacing-1;
  }

  :deep(.company-select-container),
  :deep(.base-smartgate-autocomplete-select-container) {
    margin-top: $spacing-2;
  }

  :deep(.custom-fields) {
    gap: $spacing-6;
  }
}

.messaging-options {
  margin-inline-start: -$spacing-2;
  margin-top: -$spacing-2;

  :deep(.v-selection-control) {
    flex: 0 1 auto;
  }

  :deep(.v-selection-control-group) {
    column-gap: $spacing-4;
  }
}

.whatsapp-banner {
  background-color: $color-warning-20 !important;
  color: $color-text-primary !important;
  padding: $spacing-4 !important;

  :deep(.v-banner__content) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: $spacing-4;
  }
}
</style>
